import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { getTimeSinceUpdate } from '../../utils/config';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Debounce hook for search
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

/**
 * Garbage Truck Management Component
 * Comprehensive CRUD operations for garbage trucks
 */
const VehicleManagement = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [showSMSModal, setShowSMSModal] = useState(false);
  const [smsVehicle, setSmsVehicle] = useState(null);

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${API}/admin/vehicles/all`);
      const allVehicles = [...response.data.buses, ...response.data.private_vehicles];
      setVehicles(allVehicles);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (vehicle) => {
    setEditingVehicle(vehicle);
    setShowEditModal(true);
  };

  const handleSendSMS = (vehicle) => {
    setSmsVehicle(vehicle);
    setShowSMSModal(true);
  };

  const handleAdd = () => {
    setShowAddModal(true);
  };

  const handleDelete = async (vehicleId) => {
    if (window.confirm('Are you sure you want to delete this vehicle?')) {
      try {
        await axios.delete(`${API}/admin/vehicles/${vehicleId}`);
        alert('Vehicle deleted successfully!');
        fetchVehicles();
      } catch (error) {
        alert('Failed to delete vehicle. Please try again.');
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedVehicles.length === 0) {
      alert('Please select vehicles to delete');
      return;
    }

    if (window.confirm(`Are you sure you want to delete ${selectedVehicles.length} vehicles?`)) {
      try {
        await axios.delete(`${API}/admin/bulk-delete`, {
          data: { type: 'vehicles', ids: selectedVehicles }
        });
        alert(`${selectedVehicles.length} vehicles deleted successfully!`);
        setSelectedVehicles([]);
        fetchVehicles();
      } catch (error) {
        alert('Failed to delete vehicles. Please try again.');
      }
    }
  };

  const handleSelectVehicle = (vehicleId) => {
    setSelectedVehicles(prev =>
      prev.includes(vehicleId)
        ? prev.filter(id => id !== vehicleId)
        : [...prev, vehicleId]
    );
  };

  const handleSelectAll = () => {
    if (selectedVehicles.length === filteredVehicles.length) {
      setSelectedVehicles([]);
    } else {
      setSelectedVehicles(filteredVehicles.map(v => v.id));
    }
  };

  // Get unique owners who have vehicles (from vehicle data itself)
  const vehicleOwners = vehicles
    .filter(v => v.owner_id && v.owner_name)
    .reduce((acc, v) => {
      if (!acc.find(o => o.id === v.owner_id)) {
        acc.push({ id: v.owner_id, username: v.owner_name, email: v.owner_email });
      }
      return acc;
    }, []);

  const filteredVehicles = vehicles.filter(vehicle => {
    const matchesSearch = (
      vehicle.make?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.license_plate?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.gps_device_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.owner_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.owner_email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const matchesType = filterType === 'all' ||
      (filterType === 'bus' && (vehicle.vehicle_type === 'bus' || vehicle.vehicle_type === 'truck')) ||
      (filterType === 'private' && vehicle.vehicle_type !== 'bus' && vehicle.vehicle_type !== 'truck') ||
      (filterType === 'public' && vehicle.is_public) ||
      (filterType === 'private_owned' && !vehicle.is_public);

    const matchesOwner = ownerFilter === 'all' || 
      (ownerFilter === vehicle.owner_id);

    return matchesSearch && matchesType && matchesOwner;
  });

  const VehicleCard = ({ vehicle }) => (
    <div className="border rounded-lg p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <input
            type="checkbox"
            checked={selectedVehicles.includes(vehicle.id)}
            onChange={() => handleSelectVehicle(vehicle.id)}
            className="h-4 w-4 text-blue-600 rounded border-gray-300"
          />
          <div>
            <h3 className="font-semibold text-lg text-gray-900">
              {vehicle.year} {vehicle.make} {vehicle.model}
            </h3>
            <p className="text-sm text-gray-600">License: {vehicle.license_plate}</p>
          </div>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => handleEdit(vehicle)}
            className="text-blue-600 hover:text-blue-800 text-sm px-3 py-1 border border-blue-600 rounded hover:bg-blue-50"
          >
            ✏️ Edit
          </button>
          <button
            onClick={() => handleSendSMS(vehicle)}
            className="text-green-600 hover:text-green-800 text-sm px-3 py-1 border border-green-600 rounded hover:bg-green-50"
          >
            📱 SMS
          </button>
          <button
            onClick={() => handleDelete(vehicle.id)}
            className="text-red-600 hover:text-red-800 text-sm px-3 py-1 border border-red-600 rounded hover:bg-red-50"
          >
            🗑️ Delete
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <p className="font-medium text-gray-800">Owner{vehicle.owners?.length > 1 ? 's' : ''}:</p>
          {vehicle.owners?.length > 0 ? (
            <div className="space-y-1">
              {vehicle.owners.map((owner, index) => (
                <div key={owner.id || index}>
                  <p className="text-gray-600">{owner.name || owner.email?.split('@')[0] || 'Unknown'}</p>
                  <p className="text-xs text-gray-500">{owner.email || 'No email'}</p>
                </div>
              ))}
            </div>
          ) : (
            <>
              <p className="text-gray-600">{vehicle.owner_name || 'Unknown'}</p>
              <p className="text-xs text-gray-500">{vehicle.owner_email || 'Unknown'}</p>
            </>
          )}
        </div>
        <div>
          <p className="font-medium text-gray-800">GPS Device:</p>
          <p className="text-gray-600">{vehicle.gps_device_id || 'Not assigned'}</p>
        </div>
        <div>
          <p className="font-medium text-gray-800">Type:</p>
          <p className="text-gray-600 capitalize">{vehicle.vehicle_type}</p>
        </div>
        <div>
          <p className="font-medium text-gray-800">Status:</p>
          <div className="flex flex-col space-y-1">
            <span className={`px-2 py-1 rounded-full text-xs ${vehicle.is_public ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
              }`}>
              {vehicle.is_public ? 'Public' : 'Private'}
            </span>
            <span className={`px-2 py-1 rounded-full text-xs ${vehicle.is_active === false ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
              }`}>
              {vehicle.is_active === false ? 'Inactive' : 'Active'}
            </span>
          </div>
        </div>
      </div>

      {vehicle.last_location && (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded">
          <p className="text-sm font-medium text-green-800">Last Location:</p>
          <p className="text-xs text-green-700">
            {vehicle.last_location.latitude?.toFixed(6)}, {vehicle.last_location.longitude?.toFixed(6)}
          </p>
          <p className="text-xs text-green-600">
            {getTimeSinceUpdate(vehicle.last_updated)}
          </p>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-4xl mb-4">🚛</div>
          <p className="text-gray-600">Loading garbage trucks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Garbage Truck Management</h2>
          <p className="text-gray-600 mt-1">Add, edit, and manage garbage trucks</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleAdd}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center"
          >
            ➕ Add Garbage Truck
          </button>
          {selectedVehicles.length > 0 && (
            <button
              onClick={handleBulkDelete}
              className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
            >
              🗑️ Delete Selected ({selectedVehicles.length})
            </button>
          )}
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
          <div className="flex items-center">
            <div className="text-blue-600 text-3xl mr-4">🚛</div>
            <div>
              <p className="text-sm text-blue-600 font-medium">Total Garbage Trucks</p>
              <p className="text-2xl font-bold text-blue-800">{vehicles.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-green-50 rounded-lg p-6 border border-green-200">
          <div className="flex items-center">
            <div className="text-green-600 text-3xl mr-4">✅</div>
            <div>
              <p className="text-sm text-green-600 font-medium">Active Trucks</p>
              <p className="text-2xl font-bold text-green-800">
                {vehicles.filter(v => v.is_active !== false).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-cyan-50 rounded-lg p-6 border border-cyan-200">
          <div className="flex items-center">
            <div className="text-cyan-600 text-3xl mr-4">📡</div>
            <div>
              <p className="text-sm text-cyan-600 font-medium">With GPS Tracker</p>
              <p className="text-2xl font-bold text-cyan-800">
                {vehicles.filter(v => v.gps_device_id).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter Controls */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex-1 max-w-md">
            <input
              type="text"
              placeholder="Search garbage trucks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex space-x-4">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Trucks</option>
              <option value="public">Active Trucks</option>
              <option value="private_owned">Inactive Trucks</option>
            </select>

            <button
              onClick={handleSelectAll}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              {selectedVehicles.length === filteredVehicles.length ? 'Deselect All' : 'Select All'}
            </button>

            <button
              onClick={fetchVehicles}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              🔄 Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Garbage Trucks List */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">
          Garbage Trucks ({filteredVehicles.length})
        </h3>

        {filteredVehicles.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-6xl mb-4">🚛</div>
            <p className="text-gray-500">
              {searchTerm || filterType !== 'all'
                ? 'No garbage trucks match your search criteria'
                : 'No garbage trucks found. Add your first truck!'
              }
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredVehicles.map(vehicle => (
              <VehicleCard key={vehicle.id} vehicle={vehicle} />
            ))}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <VehicleEditModal
          vehicle={editingVehicle}
          onClose={() => {
            setShowEditModal(false);
            setEditingVehicle(null);
          }}
          onUpdate={fetchVehicles}
        />
      )}

      {showAddModal && (
        <VehicleAddModal
          onClose={() => setShowAddModal(false)}
          onAdd={fetchVehicles}
        />
      )}

      {showSMSModal && smsVehicle && (
        <SMSModal
          vehicle={smsVehicle}
          onClose={() => {
            setShowSMSModal(false);
            setSmsVehicle(null);
          }}
        />
      )}
    </div>
  );
};

/**
 * Garbage Truck Edit Modal Component
 */
const VehicleEditModal = ({ vehicle, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    make: vehicle.make || '',
    model: vehicle.model || '',
    year: vehicle.year || '',
    color: vehicle.color || '',
    license_plate: vehicle.license_plate || '',
    gps_device_id: vehicle.gps_device_id || '',
    vehicle_type: vehicle.vehicle_type || 'private',
    is_public: vehicle.is_public || false,
    is_active: vehicle.is_active !== false,
    has_remote_shutdown: vehicle.has_remote_shutdown || false,
    owner_ids: vehicle.owner_ids || (vehicle.owner_id ? [vehicle.owner_id] : []),
    update_frequency: vehicle.update_frequency || 30,
    phone_number: vehicle.phone_number || '',
    date_started: vehicle.date_started ? new Date(vehicle.date_started).toISOString().split('T')[0] : '',
    monthly_cost: vehicle.monthly_cost || ''
  });

  // User search state with lazy loading
  const [ownerSearchTerm, setOwnerSearchTerm] = useState('');
  const [showOwnerDropdown, setShowOwnerDropdown] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersPage, setUsersPage] = useState(1);
  const [hasMoreUsers, setHasMoreUsers] = useState(true);
  // Support multiple owners
  const [selectedOwners, setSelectedOwners] = useState(
    vehicle.owners || (vehicle.owner_id ? [{ id: vehicle.owner_id, name: vehicle.owner_name, email: vehicle.owner_email }] : [])
  );
  const dropdownRef = useRef(null);

  // Debounce the search term
  const debouncedSearchTerm = useDebounce(ownerSearchTerm, 300);

  // Fetch users with search and pagination
  const fetchUsers = useCallback(async (search = '', page = 1, append = false) => {
    setUsersLoading(true);
    try {
      const response = await axios.get(`${API}/admin/users/all`, {
        params: { search, page, limit: 20 }
      });
      const newUsers = response.data.users || [];
      const pagination = response.data.pagination || {};

      if (append) {
        setUsers(prev => [...prev, ...newUsers]);
      } else {
        setUsers(newUsers);
      }
      setHasMoreUsers(page < (pagination.pages || 1));
      setUsersPage(page);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  // Fetch users when search term changes
  useEffect(() => {
    if (showOwnerDropdown) {
      fetchUsers(debouncedSearchTerm, 1, false);
    }
  }, [debouncedSearchTerm, showOwnerDropdown, fetchUsers]);

  // Load more users
  const loadMoreUsers = () => {
    if (!usersLoading && hasMoreUsers) {
      fetchUsers(debouncedSearchTerm, usersPage + 1, true);
    }
  };

  // Handle scroll to load more
  const handleDropdownScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 50) {
      loadMoreUsers();
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showOwnerDropdown && !event.target.closest('.owner-select-container')) {
        setShowOwnerDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showOwnerDropdown]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Prepare data for submission - convert date_started to proper format
      const submitData = { ...formData };
      if (submitData.date_started) {
        // Convert YYYY-MM-DD to ISO datetime string
        submitData.date_started = new Date(submitData.date_started + 'T00:00:00Z').toISOString();
      } else {
        // Send null if empty
        submitData.date_started = null;
      }

      // Convert monthly_cost to proper numeric value
      if (submitData.monthly_cost) {
        submitData.monthly_cost = parseFloat(submitData.monthly_cost);
      } else {
        submitData.monthly_cost = null;
      }

      await axios.put(`${API}/admin/vehicles/${vehicle.id}`, submitData);
      alert('Vehicle updated successfully!');
      onUpdate();
      onClose();
    } catch (error) {
      alert('Failed to update vehicle. Please try again.');
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Add owner to the list
  const addOwner = (user) => {
    if (!selectedOwners.find(o => o.id === user.id)) {
      const newOwners = [...selectedOwners, { id: user.id, name: user.username || user.email?.split('@')[0], email: user.email }];
      setSelectedOwners(newOwners);
      setFormData(prev => ({ ...prev, owner_ids: newOwners.map(o => o.id) }));
    }
    setOwnerSearchTerm('');
    setShowOwnerDropdown(false);
  };

  // Remove owner from the list
  const removeOwner = (ownerId) => {
    const newOwners = selectedOwners.filter(o => o.id !== ownerId);
    setSelectedOwners(newOwners);
    setFormData(prev => ({ ...prev, owner_ids: newOwners.map(o => o.id) }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Edit Garbage Truck</h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Make</label>
              <input
                type="text"
                value={formData.make}
                onChange={(e) => handleChange('make', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
              <input
                type="text"
                value={formData.model}
                onChange={(e) => handleChange('model', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
              <input
                type="number"
                value={formData.year}
                onChange={(e) => handleChange('year', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="1900"
                max="2030"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
              <input
                type="text"
                value={formData.color}
                onChange={(e) => handleChange('color', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">License Plate</label>
            <input
              type="text"
              value={formData.license_plate}
              onChange={(e) => handleChange('license_plate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">GPS Device ID</label>
            <input
              type="text"
              value={formData.gps_device_id}
              onChange={(e) => handleChange('gps_device_id', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., GPS-BZ-001"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
            <input
              type="tel"
              value={formData.phone_number}
              onChange={(e) => handleChange('phone_number', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., +1-234-567-8900"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date Started</label>
            <input
              type="date"
              value={formData.date_started}
              onChange={(e) => handleChange('date_started', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Cost</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.monthly_cost}
              onChange={(e) => handleChange('monthly_cost', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., 250.00"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Owners</label>
            <div className="relative owner-select-container">
              {/* Display selected owners as tags */}
              {selectedOwners.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {selectedOwners.map(owner => (
                    <span
                      key={owner.id}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                    >
                      {owner.name || owner.email?.split('@')[0]}
                      <button
                        type="button"
                        onClick={() => removeOwner(owner.id)}
                        className="ml-2 text-blue-600 hover:text-blue-800 focus:outline-none"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <input
                type="text"
                value={ownerSearchTerm}
                onChange={(e) => setOwnerSearchTerm(e.target.value)}
                onFocus={() => setShowOwnerDropdown(true)}
                placeholder="Search to add owner by name or email..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {showOwnerDropdown && (
                <div
                  ref={dropdownRef}
                  onScroll={handleDropdownScroll}
                  className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto"
                >
                  {usersLoading && users.length === 0 ? (
                    <div className="px-3 py-4 text-center text-gray-500">
                      <span className="animate-pulse">Loading users...</span>
                    </div>
                  ) : users.length > 0 ? (
                    <>
                      {users.filter(u => !selectedOwners.find(o => o.id === u.id)).map(user => (
                        <div
                          key={user.id}
                          onClick={() => addOwner(user)}
                          className="px-3 py-2 cursor-pointer hover:bg-blue-50"
                        >
                          <div className="font-medium">{user.username || user.email?.split('@')[0]}</div>
                          <div className="text-sm text-gray-600">{user.email}</div>
                        </div>
                      ))}
                      {usersLoading && (
                        <div className="px-3 py-2 text-center text-gray-500">
                          <span className="animate-pulse">Loading more...</span>
                        </div>
                      )}
                      {hasMoreUsers && !usersLoading && (
                        <div
                          onClick={loadMoreUsers}
                          className="px-3 py-2 text-center text-blue-600 cursor-pointer hover:bg-blue-50"
                        >
                          Load more users...
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="px-3 py-2 text-gray-500">
                      {ownerSearchTerm ? 'No users found matching your search' : 'Start typing to search users'}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Type</label>
              <select
                value={formData.vehicle_type}
                onChange={(e) => handleChange('vehicle_type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="private">Private</option>
                <option value="bus">Bus</option>
                <option value="taxi">Taxi</option>
                <option value="truck">Truck</option>
                <option value="van">Van</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Update Frequency (seconds)</label>
              <input
                type="number"
                value={formData.update_frequency}
                onChange={(e) => handleChange('update_frequency', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="5"
                max="300"
              />
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_public"
                checked={formData.is_public}
                onChange={(e) => handleChange('is_public', e.target.checked)}
                className="h-4 w-4 text-blue-600 rounded border-gray-300"
              />
              <label htmlFor="is_public" className="ml-2 text-sm text-gray-700">
                Public vehicle (visible to all users)
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => handleChange('is_active', e.target.checked)}
                className="h-4 w-4 text-green-600 rounded border-gray-300"
              />
              <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
                Active vehicle (shows on map)
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="has_remote_shutdown"
                checked={formData.has_remote_shutdown}
                onChange={(e) => handleChange('has_remote_shutdown', e.target.checked)}
                className="h-4 w-4 text-red-600 rounded border-gray-300"
              />
              <label htmlFor="has_remote_shutdown" className="ml-2 text-sm text-gray-700">
                Has Remote Shutdown (vehicle supports remote engine control)
              </label>
            </div>
          </div>

          <div className="flex justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Update Garbage Truck
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/**
 * Garbage Truck Add Modal Component
 */
const VehicleAddModal = ({ onClose, onAdd }) => {
  const [formData, setFormData] = useState({
    make: '',
    model: '',
    year: new Date().getFullYear(),
    color: '',
    license_plate: '',
    gps_device_id: '',
    vehicle_type: 'bus',  // Default to bus for garbage trucks
    is_public: true,      // Default to public (visible on map)
    is_active: true,
    has_remote_shutdown: false,
    owner_ids: [],  // Multiple owners support
    update_frequency: 30,
    phone_number: '',
    date_started: '',
    monthly_cost: ''
  });

  // User search state with lazy loading
  const [ownerSearchTerm, setOwnerSearchTerm] = useState('');
  const [showOwnerDropdown, setShowOwnerDropdown] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersPage, setUsersPage] = useState(1);
  const [hasMoreUsers, setHasMoreUsers] = useState(true);
  const [selectedOwners, setSelectedOwners] = useState([]);
  const dropdownRef = useRef(null);

  // Debounce the search term
  const debouncedSearchTerm = useDebounce(ownerSearchTerm, 300);

  // Fetch users with search and pagination
  const fetchUsers = useCallback(async (search = '', page = 1, append = false) => {
    setUsersLoading(true);
    try {
      const response = await axios.get(`${API}/admin/users/all`, {
        params: { search, page, limit: 20 }
      });
      const newUsers = response.data.users || [];
      const pagination = response.data.pagination || {};

      if (append) {
        setUsers(prev => [...prev, ...newUsers]);
      } else {
        setUsers(newUsers);
      }
      setHasMoreUsers(page < (pagination.pages || 1));
      setUsersPage(page);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  // Fetch users when search term changes
  useEffect(() => {
    if (showOwnerDropdown) {
      fetchUsers(debouncedSearchTerm, 1, false);
    }
  }, [debouncedSearchTerm, showOwnerDropdown, fetchUsers]);

  // Load more users
  const loadMoreUsers = () => {
    if (!usersLoading && hasMoreUsers) {
      fetchUsers(debouncedSearchTerm, usersPage + 1, true);
    }
  };

  // Handle scroll to load more
  const handleDropdownScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 50) {
      loadMoreUsers();
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showOwnerDropdown && !event.target.closest('.owner-select-container')) {
        setShowOwnerDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showOwnerDropdown]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Prepare data for submission
      const submitData = { ...formData };

      // Convert date_started to proper format
      if (submitData.date_started) {
        submitData.date_started = new Date(submitData.date_started + 'T00:00:00Z').toISOString();
      } else {
        submitData.date_started = null;
      }

      // Convert monthly_cost to proper numeric value
      if (submitData.monthly_cost) {
        submitData.monthly_cost = parseFloat(submitData.monthly_cost);
      } else {
        submitData.monthly_cost = null;
      }

      await axios.post(`${API}/admin/vehicles`, submitData);
      alert('Vehicle added successfully!');
      onAdd();
      onClose();
    } catch (error) {
      alert(`Failed to add vehicle: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Add owner to the list
  const addOwner = (user) => {
    if (!selectedOwners.find(o => o.id === user.id)) {
      const newOwners = [...selectedOwners, { id: user.id, name: user.username || user.email?.split('@')[0], email: user.email }];
      setSelectedOwners(newOwners);
      setFormData(prev => ({ ...prev, owner_ids: newOwners.map(o => o.id) }));
    }
    setOwnerSearchTerm('');
    setShowOwnerDropdown(false);
  };

  // Remove owner from the list
  const removeOwner = (ownerId) => {
    const newOwners = selectedOwners.filter(o => o.id !== ownerId);
    setSelectedOwners(newOwners);
    setFormData(prev => ({ ...prev, owner_ids: newOwners.map(o => o.id) }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Add New Garbage Truck</h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Make</label>
              <input
                type="text"
                value={formData.make}
                onChange={(e) => handleChange('make', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Isuzu, Hino"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
              <input
                type="text"
                value={formData.model}
                onChange={(e) => handleChange('model', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., NPR, Ranger"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
              <input
                type="number"
                value={formData.year}
                onChange={(e) => handleChange('year', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="1900"
                max="2030"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
              <input
                type="text"
                value={formData.color}
                onChange={(e) => handleChange('color', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">License Plate</label>
            <input
              type="text"
              value={formData.license_plate}
              onChange={(e) => handleChange('license_plate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">GPS Device ID</label>
            <input
              type="text"
              value={formData.gps_device_id}
              onChange={(e) => handleChange('gps_device_id', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., GPS-BZ-001"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
            <input
              type="tel"
              value={formData.phone_number}
              onChange={(e) => handleChange('phone_number', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., +1-234-567-8900"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date Started</label>
            <input
              type="date"
              value={formData.date_started}
              onChange={(e) => handleChange('date_started', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Cost</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.monthly_cost}
              onChange={(e) => handleChange('monthly_cost', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., 250.00"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Owners</label>
            <div className="relative owner-select-container">
              {/* Display selected owners as tags */}
              {selectedOwners.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {selectedOwners.map(owner => (
                    <span
                      key={owner.id}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800"
                    >
                      {owner.name || owner.email?.split('@')[0]}
                      <button
                        type="button"
                        onClick={() => removeOwner(owner.id)}
                        className="ml-2 text-green-600 hover:text-green-800 focus:outline-none"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <input
                type="text"
                value={ownerSearchTerm}
                onChange={(e) => setOwnerSearchTerm(e.target.value)}
                onFocus={() => setShowOwnerDropdown(true)}
                placeholder="Search to add owner by name or email..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {showOwnerDropdown && (
                <div
                  ref={dropdownRef}
                  onScroll={handleDropdownScroll}
                  className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto"
                >
                  {usersLoading && users.length === 0 ? (
                    <div className="px-3 py-4 text-center text-gray-500">
                      <span className="animate-pulse">Loading users...</span>
                    </div>
                  ) : users.length > 0 ? (
                    <>
                      {users.filter(u => !selectedOwners.find(o => o.id === u.id)).map(user => (
                        <div
                          key={user.id}
                          onClick={() => addOwner(user)}
                          className="px-3 py-2 cursor-pointer hover:bg-blue-50"
                        >
                          <div className="font-medium">{user.username || user.email?.split('@')[0]}</div>
                          <div className="text-sm text-gray-600">{user.email}</div>
                        </div>
                      ))}
                      {usersLoading && (
                        <div className="px-3 py-2 text-center text-gray-500">
                          <span className="animate-pulse">Loading more...</span>
                        </div>
                      )}
                      {hasMoreUsers && !usersLoading && (
                        <div
                          onClick={loadMoreUsers}
                          className="px-3 py-2 text-center text-blue-600 cursor-pointer hover:bg-blue-50"
                        >
                          Load more users...
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="px-3 py-2 text-gray-500">
                      {ownerSearchTerm ? 'No users found matching your search' : 'Start typing to search users'}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Type</label>
              <select
                value={formData.vehicle_type}
                onChange={(e) => handleChange('vehicle_type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="private">Private</option>
                <option value="bus">Bus</option>
                <option value="taxi">Taxi</option>
                <option value="truck">Truck</option>
                <option value="van">Van</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Update Frequency (seconds)</label>
              <input
                type="number"
                value={formData.update_frequency}
                onChange={(e) => handleChange('update_frequency', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="5"
                max="300"
              />
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_public_add"
                checked={formData.is_public}
                onChange={(e) => handleChange('is_public', e.target.checked)}
                className="h-4 w-4 text-blue-600 rounded border-gray-300"
              />
              <label htmlFor="is_public_add" className="ml-2 text-sm text-gray-700">
                Public vehicle (visible to all users)
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active_add"
                checked={formData.is_active}
                onChange={(e) => handleChange('is_active', e.target.checked)}
                className="h-4 w-4 text-green-600 rounded border-gray-300"
              />
              <label htmlFor="is_active_add" className="ml-2 text-sm text-gray-700">
                Active vehicle (shows on map)
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="has_remote_shutdown_add"
                checked={formData.has_remote_shutdown}
                onChange={(e) => handleChange('has_remote_shutdown', e.target.checked)}
                className="h-4 w-4 text-red-600 rounded border-gray-300"
              />
              <label htmlFor="has_remote_shutdown_add" className="ml-2 text-sm text-gray-700">
                Has Remote Shutdown (vehicle supports remote engine control)
              </label>
            </div>
          </div>

          <div className="flex justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              Add Garbage Truck
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


/**
 * SMS Modal Component
 * Send SMS commands to GPS devices (ported from RideYa admin panel)
 */
const SMSModal = ({ vehicle, onClose }) => {
  const [smsMessage, setSmsMessage] = useState('');
  const [response, setResponse] = useState('');
  const [deviceResponse, setDeviceResponse] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [commandHistory, setCommandHistory] = useState([]);
  const [currentCommandId, setCurrentCommandId] = useState(null);
  const [deviceStatus, setDeviceStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);

  useEffect(() => {
    fetchCommandHistory();
    fetchDeviceStatus();
    const statusInterval = setInterval(fetchDeviceStatus, 10000);
    return () => clearInterval(statusInterval);
  }, []);

  const fetchDeviceStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/devices/status/${vehicle.gps_device_id}`);
      setDeviceStatus(res.data);
    } catch (error) {
      setDeviceStatus({ is_online: false, status: 'unknown' });
    } finally {
      setStatusLoading(false);
    }
  };

  useEffect(() => {
    let pollInterval;
    if (isWaitingForResponse && currentCommandId) {
      pollInterval = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/admin/commands/sms/${vehicle.gps_device_id}`);
          const commands = res.data || [];
          const currentCmd = commands.find(cmd => cmd.id === currentCommandId);
          if (currentCmd?.device_response) {
            setDeviceResponse(currentCmd.device_response);
            setIsWaitingForResponse(false);
            setResponse('Success: Command sent and response received!');
            fetchCommandHistory();
          }
        } catch (error) {
          // keep polling
        }
      }, 2000);
    }
    return () => { if (pollInterval) clearInterval(pollInterval); };
  }, [isWaitingForResponse, currentCommandId, vehicle.gps_device_id]);

  useEffect(() => {
    let timeout;
    if (isWaitingForResponse) {
      timeout = setTimeout(() => {
        setIsWaitingForResponse(false);
        if (!deviceResponse) {
          setResponse(prev => prev + '\n(Device response timeout - device may not have replied)');
        }
      }, 30000);
    }
    return () => { if (timeout) clearTimeout(timeout); };
  }, [isWaitingForResponse, deviceResponse]);

  const fetchCommandHistory = async () => {
    try {
      const res = await axios.get(`${API}/admin/commands/sms/${vehicle.gps_device_id}`);
      setCommandHistory(res.data || []);
    } catch (error) {
      // ignore
    }
  };

  const handleSendSMS = async () => {
    if (!smsMessage.trim()) {
      setResponse('Error: Please enter an SMS message');
      return;
    }
    setIsLoading(true);
    setResponse('');
    setDeviceResponse('');
    setCurrentCommandId(null);
    try {
      const res = await axios.post(`${API}/admin/commands/sms`, {
        deviceId: vehicle.gps_device_id,
        message: smsMessage.trim()
      });
      if (res.data.status === 'success') {
        setResponse('Sending: Command sent, waiting for device response...');
        setCurrentCommandId(res.data.command_id);
        setIsWaitingForResponse(true);
        setSmsMessage('');
        fetchCommandHistory();
      } else {
        setResponse(`Error: ${res.data.message || 'Failed to send SMS'}`);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to send SMS command';
      setResponse(`Error: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-US', {
      timeZone: 'America/Belize',
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold text-gray-900">Send SMS Command</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
        </div>

        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="flex justify-between items-start">
            <div>
              <p className="font-semibold text-gray-900">
                {vehicle.year} {vehicle.make} {vehicle.model}
              </p>
              <p className="text-sm text-gray-600">License: {vehicle.license_plate}</p>
              <p className="text-sm text-gray-600">GPS Device: {vehicle.gps_device_id}</p>
            </div>
            <div className="text-right">
              {statusLoading ? (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                  <span className="animate-pulse">Checking...</span>
                </span>
              ) : deviceStatus?.is_online ? (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
                  Online
                </span>
              ) : (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                  Offline
                </span>
              )}
              {deviceStatus?.last_seen && (
                <p className="text-xs text-gray-500 mt-1">
                  Last seen: {new Date(deviceStatus.last_seen).toLocaleString('en-US', {
                    timeZone: 'America/Belize', month: 'short', day: 'numeric',
                    hour: '2-digit', minute: '2-digit'
                  })}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">SMS Message</label>
          <textarea
            value={smsMessage}
            onChange={(e) => setSmsMessage(e.target.value)}
            placeholder="Enter SMS command (e.g., status123456, reset123456)"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 min-h-[80px]"
            disabled={isLoading}
          />
          <p className="text-xs text-gray-500 mt-1">
            Common commands: status123456, reset123456, stop123456, resume123456
          </p>
        </div>

        <button
          onClick={handleSendSMS}
          disabled={isLoading || !smsMessage.trim()}
          className={`w-full py-2 px-4 rounded-md text-white font-medium mb-4 ${
            isLoading || !smsMessage.trim() ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
          }`}
        >
          {isLoading ? '📤 Sending...' : '📱 Send SMS Command'}
        </button>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
          <div className={`w-full px-3 py-2 border rounded-md min-h-[40px] flex items-center ${
            response.startsWith('Error')
              ? 'bg-red-50 border-red-300 text-red-700'
              : response.startsWith('Success')
                ? 'bg-green-50 border-green-300 text-green-700'
                : response.startsWith('Sending')
                  ? 'bg-yellow-50 border-yellow-300 text-yellow-700'
                  : 'bg-gray-50 border-gray-300 text-gray-600'
          }`}>
            {isWaitingForResponse && (<span className="inline-block animate-pulse mr-2">⏳</span>)}
            {response || 'Status will appear here after sending...'}
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Device Response</label>
          <div className={`w-full px-3 py-2 border rounded-md min-h-[80px] font-mono text-sm whitespace-pre-wrap ${
            deviceResponse ? 'bg-blue-50 border-blue-300 text-blue-800' : 'bg-gray-50 border-gray-300 text-gray-500'
          }`}>
            {deviceResponse || (isWaitingForResponse ? 'Waiting for device response...' : 'Device response will appear here...')}
          </div>
        </div>

        {commandHistory.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Recent Commands</label>
            <div className="border border-gray-200 rounded-md max-h-[300px] overflow-y-auto">
              {commandHistory.slice(0, 10).map((cmd, index) => (
                <div key={cmd.id || index} className={`p-3 border-b border-gray-100 last:border-b-0 ${
                  cmd.device_response ? 'bg-blue-50'
                    : cmd.status === 'acknowledged' ? 'bg-green-50'
                    : cmd.status === 'failed' ? 'bg-red-50' : 'bg-yellow-50'
                }`}>
                  <div className="flex justify-between items-start">
                    <p className="text-sm font-medium text-gray-800 truncate flex-1">{cmd.message}</p>
                    <span className={`text-xs px-2 py-1 rounded-full ml-2 ${
                      cmd.device_response ? 'bg-blue-100 text-blue-800'
                        : cmd.status === 'acknowledged' ? 'bg-green-100 text-green-800'
                        : cmd.status === 'failed' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {cmd.device_response ? 'responded' : cmd.status}
                    </span>
                  </div>
                  {cmd.device_response && (
                    <div className="mt-2 p-2 bg-white rounded border border-blue-200">
                      <p className="text-xs text-blue-600 font-medium mb-1">Device Response:</p>
                      <p className="text-xs text-blue-800 font-mono whitespace-pre-wrap">{cmd.device_response}</p>
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-1">{formatDate(cmd.sent_at || cmd.created_at)}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end mt-6">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default VehicleManagement;
